import React, { useState, useEffect, useMemo } from 'react';
import { Grid, TableContainer } from '@mui/material';
import moment from 'moment';
import QRCode from 'react-qr-code';
import { useApi } from '../ContextApi/ContextApi';
import { readFontStyle } from './fontStyle';

import '../UI/Template.css';
import Logo24_7 from '../Pages/Shared/24-7Logo/24-7logo';

const Template3 = ({ reportData = [], QRurl }) => {
  const [fontStyle, setFontStyle] = useState({});

  const currentDateTime = useMemo(() => new Date(), []);
  const username = `${sessionStorage.getItem("FirstName") || ""} ${sessionStorage.getItem("LastName") || ""}`.trim();
  const { Comment, isheader } = useApi();

  useEffect(() => {
    setFontStyle(readFontStyle());
  }, []);

  // Group report data efficiently using useMemo
  const groupedData = useMemo(() => {
    return reportData.reduce((result, item) => {
      const category = item.categorY_CODE || 'GEN';
      const subCategory = item.suB_CATEGORY_CODE || 'MAIN';

      if (!result[category]) {
        result[category] = {
          categoryValue: item.categorY_VALUE || 'General Panel',
          subCategories: {},
        };
      }

      if (!result[category].subCategories[subCategory]) {
        result[category].subCategories[subCategory] = {
          subCategoryValue: item.suB_CATEGORY_VALUE || '',
          items: [],
        };
      }

      result[category].subCategories[subCategory].items.push(item);
      return result;
    }, {});
  }, [reportData]);

  const formatTextWithLineBreaks = (text) => {
    if (!text) return '-';
    return String(text).split('\n').map((line, index, arr) => (
      <React.Fragment key={index}>
        {line}
        {index < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Status Badge Helper (High, Low, Normal)
  const getResultFlag = (range, result) => {
    if (!range || typeof range !== 'string' || result === undefined || result === null || result === '') return null;
    
    const parts = range.split('-').map((val) => Number(val.trim()));
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;

    const numResult = Number(result);
    if (isNaN(numResult)) return null;

    if (numResult < parts[0]) return { type: 'LOW', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' };
    if (numResult > parts[1]) return { type: 'HIGH', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' };

    return { type: 'NORMAL', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' };
  };

  const patient = reportData?.[0] || {};
  const labName = useMemo(() => {
    return reportData.find((x) => x.laboratoryName)?.laboratoryName || "Clinical Laboratory";
  }, [reportData]);

  const qrCodeValue = QRurl || '';
  const fontFamily = fontStyle?.fontfamily || '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif';
  const fontSize = fontStyle?.fontsize || '11px';
  const fontWeight = fontStyle?.fontweight || 'normal';

  return (
    <div
      style={{
        fontFamily,
        fontSize,
        fontWeight,
        padding: '30px 10px',
        minHeight: '100vh',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      }}
    >
      <div 
        className="print-report" 
        style={{ 
          background: '#ffffff', 
          maxWidth: '880px', 
          margin: '0 auto', 
          borderRadius: '4px',
          borderLeft: '8px solid #0369a1', // Distinctive Vertical Accent Line
          overflow: 'hidden'
        }}
      >
        {/* Top Header Banner */}
        <div style={{ padding: '28px 32px 16px 32px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#0c4a6e', fontSize, fontWeight: '800', letterSpacing: '-0.5px' }}>
                    {labName}
                  </h2>
                  <div style={{ fontSize, color: '#64748b', marginTop: '4px', lineHeight: '1.4' }}>
                    <span>{patient?.laboratoryAddress || 'Diagnostics Center'}</span> <br />
                    <span><strong>Ph:</strong> {patient?.laboratoryContactNumber || 'N/A'}</span> | <span><strong>Email:</strong> {patient?.laboratoryEmail || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </Grid>

            {/* Right Header Side: Stacked QR Code on top, Logo below */}
            <Grid item xs={4} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: '8px' }}>
              {qrCodeValue && (
                <div style={{ textAlign: 'center', border: '1px solid #e2e8f0', padding: '6px', borderRadius: '4px', backgroundColor: '#fff' }}>
                  <QRCode value={qrCodeValue} size={64} />
                  <div style={{ fontSize, color: '#94a3b8', marginTop: '2px', fontWeight: '600' }}>SCAN REPORT</div>
                </div>
              )}
              
              <div style={{ marginTop: '2px' }}>
                <Logo24_7 />
              </div>
            </Grid>
          </Grid>

          {/* Patient Details Section - Compact Minimalist Layout */}
          <div style={{ marginTop: '20px', borderTop: '2px solid #f1f5f9', borderBottom: '2px solid #f1f5f9', padding: '14px 0' }}>
            <Grid container spacing={2} style={{ fontSize }}>
              
              {/* Left Column */}
              <Grid item xs={6}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '6px 10px' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Patient Name:</span>
                  <span style={{ color: '#0f172a', fontWeight: '700', fontSize }}>{patient?.patientName || 'N/A'}</span>

                  <span style={{ color: '#64748b', fontWeight: '600' }}>MR / Lab ID:</span>
                  <span style={{ color: '#0f172a' }}>{patient?.patientID || 'N/A'} / {sessionStorage.getItem('LabID') || '-'}</span>

                  <span style={{ color: '#64748b', fontWeight: '600' }}>Age / Gender:</span>
                  <span style={{ color: '#0f172a' }}>{patient?.age ? `${patient.age} Y` : '-'} / {patient?.sex || '-'}</span>
                </div>
              </Grid>

              {/* Right Column */}
              <Grid item xs={6}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '6px 10px' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Referred By:</span>
                  <span style={{ color: '#0f172a', fontWeight: '600' }}>{patient?.referredBy || 'Self'}</span>

                  <span style={{ color: '#64748b', fontWeight: '600' }}>Contact No:</span>
                  <span style={{ color: '#0f172a' }}>{patient?.contactNumber || '-'}</span>

                  <span style={{ color: '#64748b', fontWeight: '600' }}>Report Date:</span>
                  <span style={{ color: '#0f172a', fontSize: '11px' }}>{patient?.createdAt ? moment(patient.createdAt).format('DD MMM YYYY, hh:mm A') : '-'}</span>
                </div>
              </Grid>

            </Grid>
          </div>
        </div>

        {/* Content Body */}
        <div style={{ padding: '0 32px 32px 32px' }}>
          {Object.keys(groupedData).map((category, index) => (
            <div key={index} style={{ marginBottom: '20px' }}>
              
              {/* Category Header Bar */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                borderBottom: '2px solid #0369a1', 
                paddingBottom: '4px', 
                marginBottom: '10px' 
              }}>
                <span style={{ fontSize, fontWeight: '800', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {groupedData[category].categoryValue}
                </span>
                <span style={{ fontSize, color: '#94a3b8', fontStyle: 'italic' }}>Department Profile</span>
              </div>

              {Object.keys(groupedData[category].subCategories).map((subCategory, subIndex) => (
                <div key={subIndex} style={{ marginBottom: '14px' }}>
                  {groupedData[category].subCategories[subCategory].subCategoryValue && (
                    <div style={{ fontSize, fontWeight: '700', color: '#334155', margin: '6px 0', textTransform: 'capitalize' }}>
                      &bull; {groupedData[category].subCategories[subCategory].subCategoryValue}
                    </div>
                  )}

                  <TableContainer style={{ boxShadow: 'none', background: 'transparent' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize, textTransform: 'uppercase', color: '#64748b', fontWeight: '700', width: '38%' }}>Test Name</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize, textTransform: 'uppercase', color: '#64748b', fontWeight: '700', width: '22%' }}>Observed Value</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize, textTransform: 'uppercase', color: '#64748b', fontWeight: '700', width: '15%' }}>Unit</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize, textTransform: 'uppercase', color: '#64748b', fontWeight: '700', width: '25%' }}>Reference Interval</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedData[category].subCategories[subCategory].items.map((item, itemIdx) => {
                          const flag = getResultFlag(item.normalRange, item.testResult);

                          return (
                            <React.Fragment key={itemIdx}>
                              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '8px', fontSize, color: '#0f172a', fontWeight: '600' }}>
                                  {item.testName}
                                </td>

                                {/* Value Cell with Status Badge */}
                                <td style={{ padding: '8px', fontSize, fontWeight: '700' }}>
                                  <span style={{ color: flag?.type === 'NORMAL' || !flag ? '#0f172a' : flag.color }}>
                                    {item.testResult}
                                  </span>
                                  {flag && flag.type !== 'NORMAL' && (
                                    <span style={{
                                      fontSize,
                                      fontWeight: '800',
                                      padding: '2px 5px', 
                                      borderRadius: '3px', 
                                      marginLeft: '6px', 
                                      backgroundColor: flag.bg, 
                                      color: flag.color, 
                                      border: `1px solid ${flag.border}` 
                                    }}>
                                      {flag.type}
                                    </span>
                                  )}
                                </td>

                                <td style={{ padding: '8px', fontSize, color: '#475569' }}>
                                  {formatTextWithLineBreaks(item.unit)}
                                </td>

                                <td style={{ padding: '8px', fontSize, color: '#475569' }}>
                                  {formatTextWithLineBreaks(item.normalRange)}
                                </td>
                              </tr>

                              {item.comment && item.comment.trim() !== '' && (
                                <tr>
                                  <td colSpan={4} style={{ padding: '4px 8px 8px 8px', fontSize, color: '#64748b', backgroundColor: '#f8fafc' }}>
                                    <em>Note: {item.comment}</em>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </TableContainer>
                </div>
              ))}
            </div>
          ))}

          {/* Remarks Box */}
          {Comment && (
            <div style={{ marginTop: '20px', padding: '12px', borderLeft: '3px solid #0369a1', backgroundColor: '#f8fafc', borderRadius: '0 4px 4px 0' }}>
              <div style={{ fontSize, fontWeight: '700', color: '#0369a1', textTransform: 'uppercase' }}>Clinical Comments / Interpretation</div>
              <p style={{ margin: '4px 0 0 0', fontSize, color: '#334155', lineHeight: '1.4' }}>{Comment}</p>
            </div>
          )}

          {/* Footer Metadata */}
          <div style={{ marginTop: '30px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#94a3b8' }}>
            <div>Printed: <strong>{moment(currentDateTime).format('DD-MMM-YYYY hh:mm A')}</strong></div>
            <div>Operator: <strong>{username || 'System Admin'}</strong></div>
            <div>Page 1 of 1</div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Template3;